import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ResponsiveDialog from '../common/ResponsiveDialog';
import pettyCashApi from '../../services/pettyCashApi';
import { REIMBURSE_METHODS } from '../../types/pettyCash';
import type { ClaimDetail, ClaimRow, PettyCashProcessSettings, ReimburseMethod } from '../../types/pettyCash';
import { cleanDateInput, creditText, formatAmount, reimburseCredit, round2, todayIso } from '../../utils/pettyCash';
import {
  approvalOutcome,
  bulkApprovePlan,
  groupByHolder,
  missingReferences,
  reimburseAmount,
  reimbursementOutcome,
  runOneByOne,
  type Outcome,
  type StepResult,
} from '../../utils/pettyCashBulk';

/**
 * Bulk actions of the claims list (Nov 2026): finance approves the ticked claims waiting for its decision (the last
 * finance stage posts them to SAP, one after another), and records the reimbursement of the ticked posted claims (one
 * SAP top-up journal per claim, as a single reimbursement). Each claim goes through its own request, so every check of
 * the single action applies; a refused claim is listed with the reason and the others go on.
 */

type Phase = 'form' | 'running' | 'done';

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const OUTCOME_ICON: Record<Outcome['tone'], React.ReactNode> = {
  ok: <CheckCircleOutlineIcon fontSize="small" color="success" />,
  warn: <WarningAmberIcon fontSize="small" color="warning" />,
  error: <ErrorOutlineIcon fontSize="small" color="error" />,
};

/** The claims an action is about to cover: number, a line about each, amount. */
const ClaimList: React.FC<{ rows: ClaimRow[]; amount: (r: ClaimRow) => number; subtitle: (r: ClaimRow) => string }> = ({ rows, amount, subtitle }) => (
  <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, maxHeight: 220, overflowY: 'auto', border: '1px solid #e3e8eb', borderRadius: 1 }}>
    {rows.map((r) => (
      <Box component="li" key={r.claimId} sx={{ display: 'flex', gap: 1, px: 1.5, py: 0.75, borderBottom: '1px solid #f0f2f4', '&:last-child': { borderBottom: 0 } }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600}>{r.claimNo || `Claim ${r.claimId}`}</Typography>
          <Typography variant="caption" color="text.secondary">{subtitle(r)}</Typography>
        </Box>
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{formatAmount(amount(r))}</Typography>
      </Box>
    ))}
  </Box>
);

/** What happened to each claim of a run; `held` were left out before the run (with the reason). */
const Results: React.FC<{ results: Array<{ claim: ClaimRow; outcome: Outcome }>; notTried: ClaimRow[]; held?: Array<{ row: ClaimRow; reason: string }> }> = ({ results, notTried, held = [] }) => (
  <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, maxHeight: 320, overflowY: 'auto', border: '1px solid #e3e8eb', borderRadius: 1 }}>
    {results.map(({ claim, outcome }) => (
      <Box component="li" key={claim.claimId} sx={{ display: 'flex', gap: 1, px: 1.5, py: 0.75, borderBottom: '1px solid #f0f2f4', '&:last-child': { borderBottom: 0 } }}>
        <Box sx={{ pt: 0.25 }}>{OUTCOME_ICON[outcome.tone]}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600}>{claim.claimNo || `Claim ${claim.claimId}`} · {claim.holderName}</Typography>
          <Typography variant="body2" color={outcome.tone === 'error' ? 'error' : 'text.secondary'} sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
            {outcome.text}
          </Typography>
        </Box>
      </Box>
    ))}
    {held.map(({ row, reason }) => (
      <Box component="li" key={row.claimId} sx={{ display: 'flex', gap: 1, px: 1.5, py: 0.75, borderBottom: '1px solid #f0f2f4', '&:last-child': { borderBottom: 0 } }}>
        <Box sx={{ pt: 0.25 }}>{OUTCOME_ICON.error}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600}>{row.claimNo || `Claim ${row.claimId}`} · {row.holderName}</Typography>
          <Typography variant="body2" color="error">Not approved: {reason.charAt(0).toLowerCase() + reason.slice(1)}</Typography>
        </Box>
      </Box>
    ))}
    {notTried.map((claim) => (
      <Box component="li" key={claim.claimId} sx={{ display: 'flex', gap: 1, px: 1.5, py: 0.75, borderBottom: '1px solid #f0f2f4', '&:last-child': { borderBottom: 0 } }}>
        <Box sx={{ pt: 0.25, width: 20 }} />
        <Typography variant="body2" color="text.secondary">{claim.claimNo || `Claim ${claim.claimId}`} · {claim.holderName}: not done (stopped)</Typography>
      </Box>
    ))}
  </Box>
);

const Progress: React.FC<{ verb: string; done: number; count: number; current: ClaimRow | null }> = ({ verb, done, count, current }) => (
  <Stack spacing={1}>
    <LinearProgress variant="determinate" value={count ? (done / count) * 100 : 0} aria-label={`${verb} progress`} />
    <Typography variant="body2" role="status">
      {current ? `${verb} ${Math.min(done + 1, count)} of ${count}: ${current.claimNo || `claim ${current.claimId}`}…` : `${done} of ${count} done`}
    </Typography>
  </Stack>
);

const summaryOf = (outcomes: Outcome[]) => ({
  ok: outcomes.filter((o) => o.tone === 'ok').length,
  warn: outcomes.filter((o) => o.tone === 'warn').length,
  error: outcomes.filter((o) => o.tone === 'error').length,
});

// ------------------------------------------------------------------ bulk approval

interface ApproveProps {
  open: boolean;
  rows: ClaimRow[];
  currency: string;
  /** changed: something was approved, so the list must load again. */
  onClose: (changed: boolean) => void;
}

export const BulkApproveDialog: React.FC<ApproveProps> = ({ open, rows, currency, onClose }) => {
  const plan = useMemo(() => bulkApprovePlan(rows), [rows]);
  const [postingDate, setPostingDate] = useState(todayIso());
  const [comment, setComment] = useState('');
  const [phase, setPhase] = useState<Phase>('form');
  const [progress, setProgress] = useState<{ done: number; current: ClaimRow | null }>({ done: 0, current: null });
  const [results, setResults] = useState<Array<StepResult<ClaimRow, ClaimDetail>>>([]);
  const [stopped, setStopped] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const stopRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setPostingDate(todayIso());
    setComment('');
    setPhase('form');
    setProgress({ done: 0, current: null });
    setResults([]);
    setStopped(false);
    setError(null);
    stopRef.current = false;
  }, [open]);

  const run = async () => {
    const day = cleanDateInput(postingDate);
    if (plan.posting > 0) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) { setError('Enter the posting date.'); return; }
      if (day > todayIso()) { setError('The posting date cannot be in the future.'); return; }
    }
    setError(null);
    stopRef.current = false;
    setPhase('running');
    const out = await runOneByOne(
      plan.run,
      (r) => pettyCashApi.finance(r.claimId, 'approve', comment.trim() || undefined, plan.posting > 0 ? day : undefined),
      { onProgress: (done, current) => setProgress({ done, current }), shouldStop: () => stopRef.current, fallbackError: 'The claim could not be approved' },
    );
    setResults(out.results);
    setStopped(out.stopped);
    setPhase('done');
  };

  const outcomes = results.map((r) => ({ claim: r.item, outcome: r.ok ? approvalOutcome(r.value) : { tone: 'error' as const, text: r.error || 'Not approved' } }));
  const tried = new Set(results.map((r) => r.item.claimId));
  const notTried = phase === 'done' ? plan.run.filter((r) => !tried.has(r.claimId)) : [];
  const counts = summaryOf(outcomes.map((o) => o.outcome));
  const n = plan.run.length;

  return (
    <ResponsiveDialog open={open} onClose={phase === 'running' ? undefined : () => onClose(results.some((r) => r.ok))} maxWidth="sm" fullWidth aria-labelledby="pc-bulk-approve-title">
      <DialogTitle id="pc-bulk-approve-title">{phase === 'done' ? 'Approval done' : `Approve ${plural(n, 'claim')}`}</DialogTitle>
      <DialogContent dividers>
        {phase === 'form' ? (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Each claim is approved as if you opened it: one with an error to fix is not approved, and the others go on.
              {plan.posting > 0 ? ` ${plural(plan.posting, 'claim is', 'claims are')} at the last finance stage: approved, ${plan.posting === 1 ? 'it is' : 'they are'} posted to SAP one after another.` : ''}
              {plan.moving > 0 ? ` ${plural(plan.moving, 'claim moves', 'claims move')} to the next finance stage.` : ''}
            </Typography>
            {n > 0 ? (
              <>
                <ClaimList rows={plan.run} amount={(r) => r.total} subtitle={(r) => [r.holderName, r.currentStageName].filter(Boolean).join(' · ')} />
                <Typography variant="body2" fontWeight={600}>Total {formatAmount(plan.total)} {currency}</Typography>
              </>
            ) : null}
            {plan.held.length ? (
              <Alert severity="warning">
                Not approved: {plan.held.map((h) => `${h.row.claimNo || `claim ${h.row.claimId}`} (${h.reason.toLowerCase()})`).join('; ')}.
              </Alert>
            ) : null}
            {plan.posting > 0 ? (
              <TextField
                id="pc-bulk-posting-date"
                type="date"
                label="Posting date in SAP"
                value={postingDate}
                onChange={(e) => setPostingDate(cleanDateInput(e.target.value))}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: todayIso() }}
                helperText="For the claims posted now"
                required
                fullWidth
              />
            ) : null}
            <TextField id="pc-bulk-approve-comment" label="Comment" value={comment} onChange={(e) => setComment(e.target.value.slice(0, 1000))} helperText="Optional, kept in each claim's history" multiline minRows={2} fullWidth />
          </Stack>
        ) : null}
        {phase === 'running' ? <Progress verb="Approving" done={progress.done} count={n} current={progress.current} /> : null}
        {phase === 'done' ? (
          <Stack spacing={2}>
            <Alert severity={counts.error || plan.held.length ? 'warning' : 'success'}>
              {plural(counts.ok + counts.warn, 'claim')} approved{counts.error + plan.held.length ? `, ${plural(counts.error + plan.held.length, 'claim')} not approved` : ''}.
              {stopped ? ` Stopped: ${plural(notTried.length, 'claim was', 'claims were')} not tried.` : ''}
              {outcomes.some((o) => /posting/i.test(o.outcome.text)) ? ' Posting runs in the background: the list shows when each claim is posted.' : ''}
            </Alert>
            <Results results={outcomes} notTried={notTried} held={plan.held} />
          </Stack>
        ) : null}
      </DialogContent>
      {error && phase === 'form' ? <Box sx={{ px: 3, pt: 1.5 }}><Alert severity="error" role="alert">{error}</Alert></Box> : null}
      <DialogActions>
        {phase === 'form' ? (
          <>
            <Button variant="outlined" onClick={() => onClose(false)}>Cancel</Button>
            <Button onClick={() => { void run(); }} disabled={n === 0}>Approve {plural(n, 'claim')}</Button>
          </>
        ) : null}
        {phase === 'running' ? <Button variant="outlined" onClick={() => { stopRef.current = true; }}>Stop after this claim</Button> : null}
        {phase === 'done' ? <Button onClick={() => onClose(results.some((r) => r.ok))}>Close</Button> : null}
      </DialogActions>
    </ResponsiveDialog>
  );
};

// ------------------------------------------------------------------ bulk reimbursement

interface ReimburseProps {
  open: boolean;
  rows: ClaimRow[];
  currency: string;
  /** The setup posts each top-up journal in SAP. */
  postInSap: boolean;
  accounts: Pick<PettyCashProcessSettings, 'reimburseBankAccount' | 'reimburseBankAccountName' | 'reimburseAccounts'>;
  onClose: (changed: boolean) => void;
}

export const BulkReimburseDialog: React.FC<ReimburseProps> = ({ open, rows, currency, postInSap, accounts, onClose }) => {
  const groups = useMemo(() => groupByHolder(rows), [rows]);
  const [date, setDate] = useState(todayIso());
  const [method, setMethod] = useState<ReimburseMethod>('BankTransfer');
  const [references, setReferences] = useState<Record<string, string>>({});
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const [phase, setPhase] = useState<Phase>('form');
  const [progress, setProgress] = useState<{ done: number; current: ClaimRow | null }>({ done: 0, current: null });
  const [results, setResults] = useState<Array<StepResult<{ claim: ClaimRow; key: string }, ClaimDetail>>>([]);
  const [stopped, setStopped] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const stopRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setDate(todayIso());
    setMethod('BankTransfer');
    setReferences({});
    setNote('');
    setTried(false);
    setPhase('form');
    setProgress({ done: 0, current: null });
    setResults([]);
    setStopped(false);
    setError(null);
    stopRef.current = false;
  }, [open]);

  const methodInfo = REIMBURSE_METHODS.find((m) => m.value === method);
  const needsReference = !!methodInfo?.needsReference;
  const referenceLabel = method === 'Cheque' ? 'Cheque number' : method === 'BankTransfer' ? 'Transfer reference' : 'Reference';
  const credit = postInSap ? reimburseCredit(accounts, method) : null;
  const items = groups.flatMap((g) => g.claims.map((claim) => ({ claim, key: g.key })));
  const total = round2(groups.reduce((sum, g) => sum + g.total, 0));
  const n = items.length;

  const run = async () => {
    const day = cleanDateInput(date);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) { setError('Enter the date of the payment.'); return; }
    if (day > todayIso()) { setError('The date cannot be in the future.'); return; }
    const missing = missingReferences(groups, references, needsReference);
    setTried(true);
    if (missing.length) { setError(`Type the ${referenceLabel.toLowerCase()} for ${missing.join(', ')}.`); return; }
    setError(null);
    stopRef.current = false;
    setPhase('running');
    const out = await runOneByOne(
      items,
      ({ claim, key }) => pettyCashApi.reimburse(claim.claimId, {
        amount: reimburseAmount(claim),
        date: day,
        method,
        reference: String(references[key] || '').trim() || null,
        note: note.trim() || null,
      }),
      {
        onProgress: (done, current) => setProgress({ done, current: current ? current.claim : null }),
        shouldStop: () => stopRef.current,
        fallbackError: 'The reimbursement could not be recorded',
      },
    );
    setResults(out.results);
    setStopped(out.stopped);
    setPhase('done');
  };

  const outcomes = results.map((r) => ({ claim: r.item.claim, outcome: r.ok ? reimbursementOutcome(r.value) : { tone: 'error' as const, text: r.error || 'Not recorded' } }));
  const done = new Set(results.map((r) => r.item.claim.claimId));
  const notTried = phase === 'done' ? items.filter((i) => !done.has(i.claim.claimId)).map((i) => i.claim) : [];
  const counts = summaryOf(outcomes.map((o) => o.outcome));

  return (
    <ResponsiveDialog open={open} onClose={phase === 'running' ? undefined : () => onClose(results.some((r) => r.ok))} maxWidth="sm" fullWidth aria-labelledby="pc-bulk-reimburse-title">
      <DialogTitle id="pc-bulk-reimburse-title">{phase === 'done' ? 'Reimbursements done' : `Record ${plural(n, 'reimbursement')}`}</DialogTitle>
      <DialogContent dividers>
        {phase === 'form' ? (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Each claim is paid back the receipts SAP paid, as when it is recorded on its own.{' '}
              {postInSap
                ? `PRPO posts one top-up journal per claim in SAP: debit the holder's cash account, credit ${credit ? creditText(credit) : 'the bank account set in Petty Cash Setup'}.`
                : 'Nothing is posted in SAP: this records payments made outside PRPO.'}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                id="pc-bulk-reimburse-date"
                type="date"
                label="Payment date"
                value={date}
                onChange={(e) => setDate(cleanDateInput(e.target.value))}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: todayIso() }}
                required
                fullWidth
              />
              <TextField id="pc-bulk-reimburse-method" select label="Paid by" value={method} onChange={(e) => { setMethod(e.target.value as ReimburseMethod); setError(null); }} required fullWidth>
                {REIMBURSE_METHODS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </TextField>
            </Stack>
            {groups.map((g) => (
              <Paper key={g.key} variant="outlined" sx={{ p: 1.5 }}>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="baseline">
                    <Typography variant="subtitle2" component="h3" sx={{ flexGrow: 1 }}>{g.holderName}</Typography>
                    <Typography variant="body2" fontWeight={600}>{formatAmount(g.total)} {currency}</Typography>
                  </Stack>
                  <ClaimList rows={g.claims} amount={reimburseAmount} subtitle={(r) => [r.allocationCode, r.allocationName].filter(Boolean).join(' · ')} />
                  <TextField
                    id={`pc-bulk-reference-${g.key}`}
                    label={`${referenceLabel} for ${g.holderName}`}
                    value={references[g.key] || ''}
                    onChange={(e) => { const v = e.target.value.slice(0, 100); setReferences((r) => ({ ...r, [g.key]: v })); }}
                    required={needsReference}
                    error={tried && needsReference && !String(references[g.key] || '').trim()}
                    helperText={needsReference ? (g.claims.length > 1 ? `Required: kept on each of these ${g.claims.length} claims` : 'Required') : 'Optional'}
                    size="small"
                    fullWidth
                  />
                </Stack>
              </Paper>
            ))}
            <TextField id="pc-bulk-reimburse-note" label="Note" value={note} onChange={(e) => setNote(e.target.value.slice(0, 1000))} helperText="Optional, kept on each claim" multiline minRows={2} fullWidth />
            <Typography variant="body2" fontWeight={600}>Total {formatAmount(total)} {currency} · {plural(n, 'claim')} of {plural(groups.length, 'holder')}</Typography>
          </Stack>
        ) : null}
        {phase === 'running' ? <Progress verb="Recording" done={progress.done} count={n} current={progress.current} /> : null}
        {phase === 'done' ? (
          <Stack spacing={2}>
            <Alert severity={counts.error || counts.warn ? 'warning' : 'success'}>
              {plural(counts.ok + counts.warn, 'reimbursement')} recorded{counts.error ? `, ${plural(counts.error, 'claim')} not recorded` : ''}.
              {counts.warn ? ` SAP refused ${plural(counts.warn, 'top-up journal')}: open ${counts.warn === 1 ? 'that claim' : 'those claims'} to post ${counts.warn === 1 ? 'it' : 'them'} again.` : ''}
              {stopped ? ` Stopped: ${plural(notTried.length, 'claim was', 'claims were')} not tried.` : ''}
            </Alert>
            <Results results={outcomes} notTried={notTried} />
          </Stack>
        ) : null}
      </DialogContent>
      {error && phase === 'form' ? <Box sx={{ px: 3, pt: 1.5 }}><Alert severity="error" role="alert">{error}</Alert></Box> : null}
      <DialogActions>
        {phase === 'form' ? (
          <>
            <Button variant="outlined" onClick={() => onClose(false)}>Cancel</Button>
            <Button onClick={() => { void run(); }} disabled={n === 0}>Record {plural(n, 'reimbursement')}</Button>
          </>
        ) : null}
        {phase === 'running' ? <Button variant="outlined" onClick={() => { stopRef.current = true; }}>Stop after this claim</Button> : null}
        {phase === 'done' ? <Button onClick={() => onClose(results.some((r) => r.ok))}>Close</Button> : null}
      </DialogActions>
    </ResponsiveDialog>
  );
};
